import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDarkMode } from "../contexts/DarkModeContext";
import DownloadButton from "./DownloadButton";
import { findChunkRanges } from "./chunkHighlight";
import { describeSource } from "./previewUtils";

// Chunk positions are [page, x0, x1, top, bottom] in PDF points with a
// top-left origin (y grows downward), the same units PDF.js uses at scale 1.
// Inferred from real data: text-line boxes are ~12 units tall (a ~12pt font)
// and a Letter page is ~600 units wide — both only make sense as points, not a
// 3×-rasterised pixel space. PDF.js renders at `viewport.scale` px/point, so a
// stored coordinate maps to a viewport pixel by (viewport.scale / POINTS_ZOOM).
//
// If a future corpus stores positions in a rasterised pixel space instead (e.g.
// RAGFlow's deepdoc default of zoomin=3), this single constant is where to
// adjust — confirm against the backend's chunk_highlight renderer.
const POINTS_ZOOM = 1;

const HL_COLOR = "rgba(255, 235, 59, 0.40)";

// Render the whole PDF inline with PDF.js and highlight the cited chunk. Two
// strategies, gated by `hasBbox` (mirrors ChatMessage's existing check):
//   * real bbox coordinates  -> draw highlight boxes directly from positions
//   * no/synthetic coordinates -> align the chunk text against the rendered text
//     layer (shared matcher) and box the matched runs.
//
// pdfjs-dist is lazy-imported so it doesn't bloat the bundle for users who never
// open a PDF citation. The worker is bundled locally via `new URL(...)` (webpack
// copies it into the build and serves it same-origin) rather than loaded from a
// CDN — a cross-origin worker is browser-blocked and hangs getDocument() with no
// error. We use the legacy build deliberately: the modern v4 build needs
// Promise.withResolvers (Safari < 17.4 breaks) which legacy polyfills, and since
// the import is lazy the extra weight never touches the main bundle.
export default function PdfPreviewModal({
  docName,
  fetchUrl,
  downloadUrl,
  fullDocUrl,
  source,
  provider,
  chunkContent,
  chunkPositions,
  hasBbox,
  authHeader,
  onClose,
}) {
  const { isDarkMode } = useDarkMode();
  const scrollRef = useRef(null);
  const pagesRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    let pdf = null;
    // Abort the in-flight fetch on cleanup so a closed modal (or StrictMode's
    // discarded first effect run) doesn't leave an orphan request streaming
    // against the proxy.
    const controller = new AbortController();

    const render = async () => {
      try {
        // fetchUrl is the same-origin backend proxy (?stream=1), so cookies +
        // bearer header authenticate the request and CORS is a non-issue.
        const res = await fetch(fetchUrl, {
          credentials: "include",
          headers: authHeader ? { Authorization: authHeader } : undefined,
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.arrayBuffer();
        if (cancelled) return;

        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
        // The worker file must reach the browser untouched: referencing it via
        // `new URL(..., import.meta.url)` lets CRA's babel pass transpile it,
        // which injects `@babel/runtime` ESM imports with build-machine-absolute
        // paths (`/app/node_modules/...`) — dead links in the deployed image, so
        // the worker dies on its first import and getDocument() never resolves.
        // Instead the prebuild/prestart `copy-pdf-worker` script copies the
        // worker verbatim from node_modules into public/ (CRA ships public/
        // byte-for-byte), renamed to .js so every server labels it with a
        // JavaScript MIME type, which module workers strictly require. It must
        // also stay same-origin: a cross-origin worker is browser-blocked and
        // hangs getDocument() with no error.
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
        if (cancelled || !pagesRef.current) return;

        // Uint8Array rather than ArrayBuffer: v4 deprecates raw ArrayBuffer
        // input. The buffer is transferred (detached) to the worker — fine, we
        // never reuse it.
        pdf = await pdfjsLib.getDocument({ data: new Uint8Array(data) }).promise;
        if (cancelled || !pagesRef.current) return;
        pagesRef.current.innerHTML = "";

        // Fit each page to the scroll container's width.
        const containerWidth = (scrollRef.current?.clientWidth || 900) - 32;

        // Text items across all pages, each with its global character offset and
        // its on-screen rectangle, so a matched character range can be boxed.
        const allItems = [];
        let globalLen = 0;
        const pageOverlays = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const base = page.getViewport({ scale: 1 });
          const scale = Math.max(0.5, Math.min(2.5, containerWidth / base.width));
          const viewport = page.getViewport({ scale });

          const pageEl = document.createElement("div");
          pageEl.style.position = "relative";
          pageEl.style.margin = "0 auto 16px";
          pageEl.style.width = `${viewport.width}px`;
          pageEl.style.height = `${viewport.height}px`;
          pageEl.style.boxShadow = "0 1px 6px rgba(0,0,0,0.18)";

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = "block";
          pageEl.appendChild(canvas);

          const overlayEl = document.createElement("div");
          overlayEl.style.position = "absolute";
          overlayEl.style.inset = "0";
          overlayEl.style.pointerEvents = "none";
          pageEl.appendChild(overlayEl);

          pagesRef.current.appendChild(pageEl);

          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          if (cancelled) return;

          const textContent = await page.getTextContent();
          for (const item of textContent.items) {
            if (typeof item.str !== "string" || item.str.length === 0) continue;
            // Combine the viewport and item transforms to get on-screen geometry.
            const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
            const fontHeight = Math.hypot(tx[2], tx[3]) || item.height * scale;
            const rect = {
              left: tx[4],
              top: tx[5] - fontHeight, // tx[5] is the baseline; glyph rises above it
              width: item.width * scale,
              height: fontHeight,
            };
            allItems.push({ str: item.str, globalStart: globalLen, rect, overlayEl });
            globalLen += item.str.length;
          }
          pageOverlays.push({ overlayEl, viewport, pageNum });
        }

        if (cancelled) return;

        // ---- Highlight ----
        let firstHl = null;
        const drawBox = (overlayEl, box) => {
          if (!(box.width > 0) || !(box.height > 0)) return;
          const el = document.createElement("div");
          el.style.position = "absolute";
          el.style.left = `${box.left}px`;
          el.style.top = `${box.top}px`;
          el.style.width = `${box.width}px`;
          el.style.height = `${box.height}px`;
          el.style.background = HL_COLOR;
          el.style.borderRadius = "2px";
          overlayEl.appendChild(el);
          if (!firstHl) firstHl = el;
        };

        if (hasBbox && Array.isArray(chunkPositions)) {
          // Coordinate path: one box per stored position, on its page.
          for (const pos of chunkPositions) {
            if (!Array.isArray(pos) || pos.length < 5) continue;
            const [pageNum, x0, x1, top, bottom] = pos;
            const pg = pageOverlays.find((p) => p.pageNum === pageNum);
            if (!pg) continue;
            const f = pg.viewport.scale / POINTS_ZOOM;
            drawBox(pg.overlayEl, {
              left: x0 * f,
              top: top * f,
              width: (x1 - x0) * f,
              height: (bottom - top) * f,
            });
          }
        } else if (chunkContent) {
          // Text path: align the chunk against the concatenated text layer, then
          // box each matched run within the items it overlaps.
          const flatText = allItems.map((it) => it.str).join("");
          const ranges = findChunkRanges(flatText, chunkContent);
          for (const range of ranges) {
            for (const it of allItems) {
              const itEnd = it.globalStart + it.str.length;
              if (it.globalStart >= range.end || itEnd <= range.start) continue;
              const len = it.str.length || 1;
              const f0 = Math.max(0, range.start - it.globalStart) / len;
              const f1 = Math.min(len, range.end - it.globalStart) / len;
              drawBox(it.overlayEl, {
                left: it.rect.left + f0 * it.rect.width,
                top: it.rect.top,
                width: (f1 - f0) * it.rect.width,
                height: it.rect.height,
              });
            }
          }
        }

        setStatus("ready");
        if (firstHl) {
          requestAnimationFrame(() => {
            firstHl.scrollIntoView({ behavior: "smooth", block: "center" });
          });
        }
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setErrMsg(e?.message || String(e));
        }
      }
    };

    render();
    return () => {
      cancelled = true;
      controller.abort();
      if (pdf) {
        try { pdf.destroy(); } catch { /* noop */ }
      }
    };
  }, [fetchUrl, chunkContent, hasBbox, chunkPositions, authHeader]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const sourceLabel = describeSource(source, provider);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview of ${docName}`}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className={`relative w-full max-w-5xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${
            isDarkMode ? "bg-slate-800" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-3 border-b ${
            isDarkMode ? "border-slate-700" : "border-gray-200"
          }`}>
            <div className="flex-1 mr-4 min-w-0">
              <h3 className={`text-sm font-semibold truncate ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}>{docName}</h3>
              <div className={`text-xs mt-0.5 truncate ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}>
                Inline preview &middot; full document hosted on {sourceLabel}
              </div>
              {(fullDocUrl || downloadUrl) && (
                <a
                  href={fullDocUrl || downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={fullDocUrl || downloadUrl}
                  className={`block text-[11px] mt-0.5 truncate font-mono underline ${
                    isDarkMode ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"
                  }`}
                >
                  {fullDocUrl || downloadUrl}
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-slate-700 text-slate-400 hover:text-slate-200"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div ref={scrollRef} className="overflow-auto p-4 flex-1 min-h-[200px] bg-gray-100">
            {status === "loading" && (
              <div className="flex items-center justify-center py-16 text-gray-600 text-sm">
                Loading document…
              </div>
            )}
            {status === "error" && (
              <div className="text-red-700 text-sm p-4">
                Couldn't render this document.{" "}
                <DownloadButton
                  url={downloadUrl}
                  authHeader={authHeader}
                  filename={docName}
                  className="underline"
                />
                .
                {errMsg && <div className="text-xs mt-2 opacity-70 break-all">{errMsg}</div>}
              </div>
            )}
            <div ref={pagesRef} className={status === "ready" ? "" : "hidden"} />
          </div>
        </div>
      </div>
    </>
  );
}

PdfPreviewModal.propTypes = {
  docName: PropTypes.string.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  downloadUrl: PropTypes.string.isRequired,
  fullDocUrl: PropTypes.string,
  source: PropTypes.oneOf(["connector", "local", "proxy"]).isRequired,
  provider: PropTypes.string,
  chunkContent: PropTypes.string,
  chunkPositions: PropTypes.array,
  hasBbox: PropTypes.bool,
  authHeader: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
